import ClickAwayListener from "@mui/material/ClickAwayListener";
import { COUNTRY_CURRENCIES } from "@redotech/money/countries";
import { CountryCurrency } from "@redotech/money/country";
import { IterableMap } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { getKey } from "@redotech/react-util/key";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import ChevronDown from "@redotech/redo-web/icon/chevron-down.svg";
import LeftArrow from "@redotech/redo-web/icon/left-arrow.svg";
import * as link from "@redotech/redo-web/link.module.css";
import { sleep } from "@redotech/util/schedule";
import * as classNames from "classnames";
import { memo, useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SetAuthContext } from "../app/auth";
import { CurrencyContext } from "../app/currency";
import { OrderContext } from "../app/order";
import { SettingsContext } from "../app/settings";
import { SetUserContext, UserContext } from "../app/user";
import { AdvancedExchangeContext } from "../contexts/AdvancedExchangeContext";
import { CurrentItemBeingReturnedContext } from "../contexts/CurrentItemBeingReturnedContext";
import { ExchangeTypeContext } from "../contexts/ExchangeTypeContext";
import { FlowStep, FlowStepContext } from "../contexts/FlowStepContext";
import { ItemsReadyForReturnContext } from "../contexts/ItemsReadyForReturnContext";
import { StorefrontCartContext } from "../contexts/StorefrontCartContext";
import { calculateReturnTotals } from "../utils/utils";
import * as navbarCss from "./navbar.module.css";
import * as page from "./page.module.css";

export const Navbar = memo(({ className = "" }: { className?: string }) => {
  const navigate = useNavigate();
  const user = useContext(UserContext);
  const setUser = useContext(SetUserContext);
  const setAuth_ = useContext(SetAuthContext);
  const settings = useContext(SettingsContext);
  const { flowStep, setFlowStep, submitting } = useContext(FlowStepContext);
  const { setExchangeType, setExchangeGroup } = useContext(ExchangeTypeContext);
  const currentOrder = useContext(OrderContext);
  const { currentItemBeingReturned, setCurrentItemBeingReturned } = useContext(
    CurrentItemBeingReturnedContext,
  );
  const {
    itemsReadyForReturn,
    availableReturnOptions,
    setAvailableReturnOptions,
    setItemsReadyForReturn,
  } = useContext(ItemsReadyForReturnContext);
  const size = useScreenType();
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [storeCredit, setStoreCredit] = useState(0);
  const {
    cartVisible,
    setCartMenuOpen,
    setCartVisible,
    setSelectedSuggestedProduct,
  } = useContext(AdvancedExchangeContext);
  const { shopifyStorefrontCart } = useContext(StorefrontCartContext);
  const { currency, formatCurrency } = useContext(CurrencyContext);

  useEffect(() => {
    if (cartVisible) {
      calculateTotals();
    }
  }, [shopifyStorefrontCart, cartVisible]);

  const calculateTotals = () => {
    const returnTotals = calculateReturnTotals(
      itemsReadyForReturn,
      currentOrder,
      settings,
      shopifyStorefrontCart,
    );
    setStoreCredit(
      returnTotals.totalStoreCredit + returnTotals.nonAccruableLeftover,
    );
  };

  const getInitials = (string: string) => {
    return string
      .split(" ", 2)
      .map((name) => name.slice(0, 1).toUpperCase())
      .join("");
  };

  if (!user) {
    return <></>;
  }
  return (
    <div className={classNames(className, page.navBarContent)}>
      <div
        className={page.backArrow}
        onClick={() => {
          if (flowStep === FlowStep.CONFIRMATION || submitting) {
            return;
          }
          setCartVisible(false);
          setCartMenuOpen(false);
          setExchangeType(undefined);
          setSelectedSuggestedProduct(undefined);
          setExchangeGroup(undefined);
          if (flowStep === FlowStep.ITEMS) {
            if (
              !!availableReturnOptions?.shopNow ||
              !!availableReturnOptions?.refund ||
              !!availableReturnOptions?.storeCredit
            ) {
              setAvailableReturnOptions({});
              setCurrentItemBeingReturned(undefined);
            } else if (currentItemBeingReturned) {
              // used for mobile
              setCurrentItemBeingReturned(undefined);
            }
            navigate(-1);
          } else if (flowStep === FlowStep.REVIEW) {
            // prevent the page from automatically going to the review page if the user manually goes back
            const numAvailableReturnOptions = Object.values(
              availableReturnOptions,
            ).filter((option: any) => option).length;
            if (
              itemsReadyForReturn.length === 1 &&
              numAvailableReturnOptions <= 1 &&
              currentOrder.returnableItems.length === 1
            ) {
              setItemsReadyForReturn([]);
            }
            setAvailableReturnOptions({});
            setFlowStep(FlowStep.ITEMS);
          }
        }}
      >
        {flowStep !== FlowStep.CONFIRMATION && !submitting && <StoreReturn />}
      </div>
      <div className={page.imageWrapper}>
        <a href={`https://${settings.storeUrl}`} target="_blank">
          <img
            id="navbar-logo"
            src={settings.theme?.logo_image}
            className={page.navBarLogo}
          />
        </a>
      </div>
      <div className={page.sideButtons}>
        {size === ScreenType.DESKTOP && (
          <div
            className={classNames(page.cartContainer, {
              [navbarCss.invisible]: !cartVisible,
            })}
            onClick={() => {
              if (cartVisible) {
                setCartMenuOpen(true);
              }
            }}
          >
            {storeCredit > 0 && (
              <div className={classNames(page.cartTotalText, page.textSuccess)}>
                {formatCurrency(storeCredit)}
              </div>
            )}
            <div className={page.cartSizeBubble}>
              {shopifyStorefrontCart?.lines?.edges?.length || 0}
            </div>
            <svg
              className={page.cart}
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#000000"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="10" cy="20.5" r="1" />
              <circle cx="18" cy="20.5" r="1" />
              <path d="M2.5 2.5h3l2.7 12.4a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.6l1.6-8.4H7.1" />
            </svg>
          </div>
        )}
        <div className={page.account}>
          {size === ScreenType.DESKTOP ? (
            <div className={page.accountText}>
              <div className={page.nameText}>
                {/*Show guest name if there is one*/}
                {user.guestName || user.name}
              </div>
              {currency && <CurrencySelect />}
            </div>
          ) : (
            currency && <CurrencySelect />
          )}

          <div
            className={page.avatarContainer}
            onClick={() =>
              setAccountDropdownOpen(accountDropdownOpen ? false : true)
            }
          >
            <div className={page.avatar}>
              {getInitials(user.guestName || user.name)}
            </div>
            <div className={page.chevronDown}>
              <ChevronDown />
            </div>
          </div>

          {accountDropdownOpen && (
            <ClickAwayListener
              onClickAway={() => setAccountDropdownOpen(false)}
            >
              <div className={page.accountDropdown}>
                {!user.guestEmail && (
                  <div className={page.accountMenuListItem}>
                    <Link to={"/returns-portal/orders"}>My orders</Link>
                  </div>
                )}
                <div
                  className={page.accountMenuListItem}
                  onClick={async () => {
                    setAuth_(undefined);
                    setUser(undefined);
                    await sleep(new Temporal.Duration());
                    navigate("/returns-portal/login");
                  }}
                >
                  Logout
                </div>
              </div>
            </ClickAwayListener>
          )}
        </div>
      </div>
    </div>
  );
});

const CurrencyOption = memo(
  ({ currency, onClick }: { currency: CountryCurrency; onClick(): void }) => {
    return (
      <button
        className={navbarCss.currencyOption}
        onClick={onClick}
        type="button"
      >
        {currency.currencyCode} | {currency.country}
      </button>
    );
  },
);

const CurrencySelect = memo(() => {
  const screenSize = useScreenType();
  const { currency, setCurrency } = useContext(CurrencyContext);
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  return (
    <>
      <div
        className={classNames(
          navbarCss.currencyText,
          screenSize === ScreenType.MOBILE && navbarCss.mobile,
        )}
        onClick={() => {
          setCurrencyDropdownOpen((value) => !value);
        }}
      >
        {screenSize === ScreenType.DESKTOP ? (
          <>
            {currency.currencyCode} | {currency.country}
          </>
        ) : (
          currency.currencyCode
        )}
      </div>
      {currencyDropdownOpen && (
        <ClickAwayListener onClickAway={() => setCurrencyDropdownOpen(false)}>
          <div className={navbarCss.currencyDropdown}>
            <IterableMap
              items={COUNTRY_CURRENCIES.all()}
              keyFn={(currency) => getKey(currency)}
            >
              {(currency) => {
                const handleClick = useHandler(() => {
                  setCurrency(currency);
                  setCurrencyDropdownOpen(false);
                });
                return (
                  <CurrencyOption currency={currency} onClick={handleClick} />
                );
              }}
            </IterableMap>
          </div>
        </ClickAwayListener>
      )}
    </>
  );
});

const StoreReturn = memo(({ className = "" }: { className?: string }) => {
  const screenSize = useScreenType();

  return (
    <a className={classNames(className, page.storeReturn, link.link)}>
      <LeftArrow />
      {screenSize === ScreenType.DESKTOP && "Back"}
    </a>
  );
});
